import React from "react";
import { Link } from "react-router-dom";
import { Facebook, Twitter, Instagram, Linkedin, Youtube, Mail, Phone, MapPin, Send } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { useData, API_BASE } from "../DataContext";
import { useToast } from "../hooks/use-toast";

export default function Footer() {
  const { PRODUCT_CATEGORIES } = useData();
  const { toast } = useToast();
  const [email, setEmail] = React.useState("");
  const [subscribing, setSubscribing] = React.useState(false);

  const onSubscribe = async (e) => {
    e.preventDefault();
    setSubscribing(true);
    try {
      const res = await fetch(`${API_BASE}/api/newsletter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email })
      });
      if (!res.ok) throw new Error("failed");
      toast({ title: "Subscribed!", description: "You'll receive BAISHALII updates in your inbox." });
      setEmail("");
    } catch {
      toast({ title: "Subscription failed", description: "Please try again later.", variant: "destructive" });
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <footer className="bg-[#061a3d] text-slate-300 pt-16 pb-6">
      <div className="max-w-7xl mx-auto px-6">
        {/* newsletter */}
        <div className="bg-gradient-to-r from-[#0a2a5e] to-sky-700 rounded-2xl p-8 md:p-10 mb-14 flex flex-col md:flex-row items-center justify-between gap-6 shadow-xl">
          <div>
            <h3 className="text-white text-2xl font-bold mb-1">Subscribe to BAISHALII Insights</h3>
            <p className="text-sky-100 text-sm">Get industry alerts, news and product updates straight to your inbox.</p>
          </div>
          <form onSubmit={onSubscribe} className="flex w-full md:w-auto gap-2">
            <Input type="email" placeholder="your@email.com" required value={email} onChange={(e) => setEmail(e.target.value)} data-testid="newsletter-email-input" className="bg-white/90 border-0 text-slate-900 w-full md:w-72" />
            <Button type="submit" disabled={subscribing} data-testid="newsletter-subscribe-btn" className="bg-white text-[#0a2a5e] hover:bg-sky-50 font-semibold">
              <Send className="h-4 w-4 mr-1.5" /> {subscribing ? "..." : "Subscribe"}
            </Button>
          </form>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 mb-12">
          <div>
            <div className="mb-4">
              <img src="/baishalii-logo-white.png" alt="BAISHALII — A name that justifies TRUST" className="h-14 w-auto" data-testid="footer-logo" />
            </div>
            <p className="text-sm text-slate-400 leading-relaxed mb-5">India's trusted manufacturer of PVC, CPVC and uPVC piping solutions for plumbing, drainage, agriculture and industrial applications.</p>
            <div className="flex gap-2">
              {[Facebook, Twitter, Instagram, Linkedin, Youtube].map((Icon, i) => (
                <a key={i} href="#" className="h-9 w-9 rounded-full bg-white/5 hover:bg-sky-500 flex items-center justify-center transition-colors" aria-label="social">
                  <Icon className="h-4 w-4 text-white" />
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 text-sm tracking-wider">PRODUCTS</h4>
            <ul className="space-y-2.5 text-sm">
              {PRODUCT_CATEGORIES.slice(0, 6).map((p) => (
                <li key={p.id}>
                  <Link to={`/products#${p.id}`} className="hover:text-sky-300 transition-colors">{p.name}</Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 text-sm tracking-wider">COMPANY</h4>
            <ul className="space-y-2.5 text-sm">
              <li><Link to="/about" className="hover:text-sky-300 transition-colors">About Us</Link></li>
              <li><Link to="/about#vision" className="hover:text-sky-300 transition-colors">Vision &amp; Values</Link></li>
              <li><Link to="/about#timeline" className="hover:text-sky-300 transition-colors">Our Journey</Link></li>
              <li><Link to="/contact" className="hover:text-sky-300 transition-colors">Careers</Link></li>
              <li><Link to="/contact" className="hover:text-sky-300 transition-colors">Dealer Locator</Link></li>
              <li><Link to="/contact" className="hover:text-sky-300 transition-colors">Piping Guidelines</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4 text-sm tracking-wider">CONTACT US</h4>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <div>
                  <div className="text-white font-semibold mb-0.5">Kolkata (HQ)</div>
                  <span>39 Strand Road, Room No. 25, Kolkata 700001</span>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <div>
                  <div className="text-white font-semibold mb-0.5">Howrah (Unit 1)</div>
                  <span>Vill &amp; P. O. Lakshamanpur, P. S. Domjur, Howrah, West Bengal 711114</span>
                </div>
              </li>
              <li className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 text-sky-400 shrink-0" />
                <div>
                  <div className="text-white font-semibold mb-0.5">Hooghly (Unit 2)</div>
                  <span>49 TN Mukherjee Road, Dankuni, Hooghly, West Bengal 712245</span>
                </div>
              </li>
              <li className="flex items-center gap-2.5 pt-1">
                <Phone className="h-4 w-4 text-sky-400 shrink-0" />
                <a href="tel:18002023360" className="hover:text-sky-300">Toll-free: 1800 202 3360</a>
              </li>
              <li className="flex items-center gap-2.5">
                <Mail className="h-4 w-4 text-sky-400 shrink-0" />
                <a href="mailto:contact@baishalii.in" className="hover:text-sky-300">contact@baishalii.in</a>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/10 pt-6 flex flex-col md:flex-row justify-between items-center text-xs text-slate-400 gap-3">
          <p>&copy; {new Date().getFullYear()} Baishalii Tanks &amp; Pipes. All rights reserved</p>
          <div className="flex gap-5">
            <Link to="/privacy-policy" data-testid="footer-privacy-link" className="hover:text-sky-300">Privacy Policy</Link>
            <Link to="/terms-of-use" data-testid="footer-terms-link" className="hover:text-sky-300">Terms of Use</Link>
            <Link to="/sitemap" data-testid="footer-sitemap-link" className="hover:text-sky-300">Sitemap</Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
